import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "How does the process work?",
    a: "We start with a short call about your trade and the jobs you want more of, then design a concept around your customers. You review it, we build it, and your site goes live with quote forms wired straight to your phone and inbox.",
  },
  {
    q: "Can I see what my website would look like before I buy?",
    a: "Yes. Every trade page on this site shows example concepts for Starter, Growth and Premium packages — tap 'View example site' on any package. Your real build is then customized to your business, photos, and service area.",
  },
  {
    q: "Do I own my website?",
    a: "You own your content, your photos, your logo, and your domain. We handle the technical side — hosting, security, updates, and maintenance — so you never have to think about it.",
  },
  {
    q: "What if I already have a website?",
    a: "Most of our clients do. We rebuild it from the ground up — sharper design, faster load times, and a quote funnel that actually converts — then move everything over with no downtime.",
  },
  {
    q: "I'm not tech-savvy. Is this hard to manage?",
    a: "You won't manage anything. Send us new project photos or text edits and we handle them. Leads arrive as plain emails and calls, the same way they do today — just more of them.",
  },
  {
    q: "Which trades do you build for?",
    a: "General contracting, roofing and HVAC, junk removal, pressure washing, landscaping, plumbing, painting, electrical, concrete, and tree service — each with its own demo you can explore on this site.",
  },
];

function Item({ f, open, onToggle, index }) {
  return (
    <div className="rounded-2xl border gold-border-subtle bg-surface/60 overflow-hidden">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-6 px-7 py-6 text-left group"
      >
        <span className={`font-serif text-xl sm:text-2xl transition-colors duration-300 ${open ? "gold-text" : "text-[#F8F6F0] group-hover:text-gold"}`}>
          {f.q}
        </span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-gold">
          <Plus size={20} strokeWidth={1.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="px-7 pb-7 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-3xl">{f.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="py-28 lg:py-36 bg-surface/30 border-t gold-border-subtle">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">Straight Answers</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8F6F0] tracking-tight">
            Everything owners <span className="gold-text italic">ask us first.</span>
          </h2>
        </motion.div>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <Item key={f.q} f={f} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

export const FAQ_JSONLD = faqs;
